// @ts-nocheck
import React, {
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
} from 'react';
import { Paper, Table, TableBody } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';

import {
  useTableDispatch,
  useTableState,
} from '../../context/TableSchemaBuilderContext';
import Cell from '../Cell/Cell';
import ColumnHeader from '../Header/ColumnHeader';
import RowHeader from '../Header/RowHeader';
import CornerCell from '../Cell/CornerCell';
import { useTableModification } from '../../hooks/useTableModification';
import { useTableKeyboard } from '../../hooks/useTableKeyboard';
import { useCellRefs } from '../../hooks/useCellRefs';
import { parseCellId, getColumnName } from '../../../../utils/Utils';

/**
 * Time-sliced virtualized table grid for smooth scrolling performance
 */
const TimeSlicedVirtualizedTableGrid = () => {
  const { schema, config } = useTableState();
  const dispatch = useTableDispatch();
  const { handleAddRowBefore, handleAddColumnBefore } = useTableModification();
  const { setCellRef } = useCellRefs();

  const containerRef = useRef();
  const [renderedCells, setRenderedCells] = useState([]);
  const renderQueueRef = useRef([]);
  const renderingRef = useRef(false);
  const previousCellQueueKeyRef = useRef('');

  const readOnly = config?.readOnly || false;
  const DEFAULT_WIDTH = 100;
  const DEFAULT_HEIGHT = 40;

  useTableKeyboard();

  // Get current selection for scrolling to active cell
  const { ui } = useTableState();
  const currentCell = ui?.selection?.currentCell;

  // Set initial selection
  useEffect(() => {
    const firstCellId = 'A1';
    dispatch({
      type: 'UPDATE_SELECTION',
      payload: {
        currentCell: firstCellId,
        cells: [firstCellId],
        anchorCell: firstCellId,
      },
    });
  }, [dispatch]);

  // Gets column width from schema settings or uses default width
  const getColumnWidth = useCallback(
    (columnName) => {
      return schema?.columnSettings?.[columnName]?.width || DEFAULT_WIDTH;
    },
    [schema?.columnSettings]
  );

  // Calculate dimensions
  const dataRows = schema?.rows || 5;
  const dataColumns = schema?.columns || 5;
  const totalRows = readOnly ? dataRows : dataRows + 1;
  const totalColumns = readOnly ? dataColumns : dataColumns + 1;

  // Virtualizers with proper resize support
  const rowVirtualizer = useVirtualizer({
    count: totalRows,
    getScrollElement: () => containerRef.current,
    estimateSize: (index) => {
      if (index === 0 && !readOnly) return DEFAULT_HEIGHT;
      // Get custom row height from schema if available
      const rowHeight = schema?.rowSettings?.[index]?.height;
      return rowHeight || DEFAULT_HEIGHT;
    },
    overscan: 1, // Minimal overscan for performance
  });

  const columnVirtualizer = useVirtualizer({
    count: totalColumns,
    getScrollElement: () => containerRef.current,
    estimateSize: (index) => {
      if (index === 0 && !readOnly) return 50; // Row header width
      const dataColumnIndex = readOnly ? index + 1 : index;
      const columnName = getColumnName(dataColumnIndex);
      return getColumnWidth(columnName);
    },
    horizontal: true,
    overscan: 1,
  });

  /**
   * Time-sliced cell renderer for optimal performance in large virtualized tables
   *
   * This function implements a sophisticated time-slicing mechanism to prevent UI blocking
   * when rendering large numbers of table cells. Instead of rendering all cells at once
   * (which could freeze the browser), it processes cells in small batches over multiple
   * animation frames.
   *
   * Key Performance Optimizations:
   * - **Frame Budget Control**: Limits processing to 8ms per frame to maintain 60fps
   * - **Batch Processing**: Renders 10 cells at a time for optimal balance of speed/smoothness
   * - **Non-blocking**: Uses scheduler.postTask (when available) or setTimeout for async processing
   * - **Early Exit**: Prevents duplicate rendering attempts via renderingRef guard
   *
   * How it works:
   * 1. Checks if rendering is already in progress or queue is empty (early exit)
   * 2. Sets rendering flag to prevent concurrent execution
   * 3. Processes cells in batches within the frame budget (8ms)
   * 4. Updates rendered cells state with the current batch
   * 5. Schedules next batch processing if more cells remain
   * 6. Clears rendering flag when queue is empty
   *
   * Performance Benefits:
   * - Maintains responsive UI during large table renders
   * - Prevents main thread blocking that could cause janky scrolling
   * - Allows user interactions to remain smooth during cell rendering
   * - Optimizes for both speed (larger batches) and smoothness (time budgets)
   *
   * @dependencies
   * - renderingRef.current: Prevents concurrent rendering attempts
   * - renderQueueRef.current: Queue of cell data to be rendered
   * - setRenderedCells: State setter for accumulating rendered cell elements
   *
   * @see https://web.dev/optimize-long-tasks/ for time-slicing best practices
   */
  const renderCellsInBatches = useCallback(() => {
    // Guard clause: Exit if already rendering or no cells to process
    if (renderingRef.current || renderQueueRef.current.length === 0) {
      return;
    }

    // Set rendering flag to prevent concurrent execution
    renderingRef.current = true;

    // Performance constants for optimal frame utilization
    const BATCH_SIZE = 10; // Process 10 cells per batch for smooth performance
    const FRAME_BUDGET = 8; // 8ms budget per frame to maintain 60fps (leaves 8ms for other work)

    /**
     * Internal batch processor function
     * Processes cells within the allocated frame budget and schedules continuation
     */
    const processBatch = () => {
      const startTime = performance.now();
      const batch = [];
      let processedCount = 0;

      // Process cells while we have items, haven't hit batch limit, and are within time budget
      while (
        renderQueueRef.current.length > 0 &&
        processedCount < BATCH_SIZE &&
        performance.now() - startTime < FRAME_BUDGET
      ) {
        const cellData = renderQueueRef.current.shift();
        if (cellData) {
          batch.push(cellData);
          processedCount++;
        }
      }

      // Add the processed batch to rendered cells if we have any
      if (batch.length > 0) {
        setRenderedCells((prev) => [...prev, ...batch]);
      }

      // Schedule next batch if more cells remain in queue
      if (renderQueueRef.current.length > 0) {
        // Prefer modern scheduler API for better prioritization, fallback to setTimeout
        if (typeof scheduler !== 'undefined' && scheduler.postTask) {
          scheduler.postTask(processBatch, { priority: 'user-blocking' });
        } else {
          setTimeout(processBatch, 0);
        }
      } else {
        // All cells processed, clear rendering flag
        renderingRef.current = false;
      }
    };

    // Start the batch processing
    processBatch();
  }, []);

  // Create cell data with proper styling and positioning
  const createCellData = useCallback(
    (virtualRow, virtualColumn) => {
      const i = virtualRow.index;
      const j = virtualColumn.index;
      let cellContent = null;
      let cellKey = '';

      if (!readOnly && i === 0 && j === 0) {
        cellKey = 'corner';
        cellContent = (
          <CornerCell
            onAddRowBefore={handleAddRowBefore}
            onAddColumnBefore={handleAddColumnBefore}
            heightFromVirtualized={DEFAULT_HEIGHT}
          />
        );
      } else if (!readOnly && i === 0) {
        cellKey = `col-${j}`;
        const columnName = getColumnName(j);
        cellContent = <ColumnHeader index={j} columnName={columnName} />;
      } else if (!readOnly && j === 0) {
        cellKey = `row-${i}`;
        cellContent = <RowHeader index={i} />;
      } else {
        const actualRow = readOnly ? i + 1 : i;
        const actualCol = readOnly ? j + 1 : j;
        const columnName = getColumnName(actualCol);
        const cellId = `${columnName}${actualRow}`;
        cellKey = cellId;
        cellContent = (
          <Cell
            cellId={cellId}
            columnName={columnName}
            rowIndex={actualRow}
            setCellRef={setCellRef}
            heightFromVirtualized={DEFAULT_HEIGHT}
          />
        );
      }

      return {
        key: cellKey,
        element: (
          <div
            key={cellKey}
            style={{
              position: 'absolute',
              top: virtualRow.start,
              left: virtualColumn.start,
              width: virtualColumn.size,
              height: virtualRow.size,
              boxSizing: 'border-box',
              overflow: 'visible',
              zIndex: !readOnly && (i === 0 || j === 0) ? 10 : 1,
            }}
          >
            {cellContent}
          </div>
        ),
      };
    },
    [readOnly, handleAddRowBefore, handleAddColumnBefore, setCellRef]
  );

  // Update cells when virtual items change
  const virtualRows = rowVirtualizer.getVirtualItems();
  const virtualColumns = columnVirtualizer.getVirtualItems();

  // Memoize cell queue creation with proper dependencies
  const cellQueue = useMemo(() => {
    const newQueue = [];
    for (const virtualRow of virtualRows) {
      for (const virtualColumn of virtualColumns) {
        newQueue.push(createCellData(virtualRow, virtualColumn));
      }
    }
    return newQueue;
  }, [virtualRows, virtualColumns, createCellData]);

  // Create a stable cell queue key to detect actual changes
  const cellQueueKey = useMemo(() => {
    const rowsKey = virtualRows
      .map((r) => r.index + '-' + r.start + '-' + r.size)
      .join('|');
    const columnsKey = virtualColumns
      .map((c) => c.index + '-' + c.start + '-' + c.size)
      .join('|');
    return rowsKey + '_' + columnsKey;
  }, [virtualRows, virtualColumns]);

  useEffect(() => {
    // Update cells when virtual items change
    previousCellQueueKeyRef.current = cellQueueKey;

    // Clear and update cells
    setRenderedCells([]);
    renderQueueRef.current = [];
    renderingRef.current = false;

    renderQueueRef.current = cellQueue;
    renderCellsInBatches();
  }, [cellQueueKey, cellQueue, renderCellsInBatches]);

  // Scroll to selected cell
  useEffect(() => {
    if (currentCell && containerRef.current) {
      const [colIndex, rowIndex] = parseCellId(currentCell);
      const virtualRowIndex = readOnly ? rowIndex - 1 : rowIndex;
      const virtualColIndex = readOnly ? colIndex - 1 : colIndex;

      if (virtualRowIndex >= 0) {
        rowVirtualizer.scrollToIndex(virtualRowIndex, {
          align: 'auto',
          behavior: 'smooth',
        });
      }

      if (virtualColIndex >= 0) {
        columnVirtualizer.scrollToIndex(virtualColIndex, {
          align: 'auto',
          behavior: 'smooth',
        });
      }
    }
  }, [currentCell, rowVirtualizer, columnVirtualizer, readOnly]);

  // Measure virtualizers on mount and when schema changes
  useEffect(() => {
    if (containerRef.current) {
      rowVirtualizer.measure();
      columnVirtualizer.measure();
    }
  }, [rowVirtualizer, columnVirtualizer]);

  // Re-measure virtualizers when schema dimensions or settings change
  useEffect(() => {
    // Trigger re-measurement when column settings change (for resizing)
    if (rowVirtualizer && columnVirtualizer) {
      // Use a small delay to ensure DOM has updated
      const timer = setTimeout(() => {
        rowVirtualizer.measure();
        columnVirtualizer.measure();
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [
    schema?.columnSettings,
    schema?.rowSettings,
    rowVirtualizer,
    columnVirtualizer,
  ]);

  const totalHeight = rowVirtualizer.getTotalSize();
  const totalWidth = columnVirtualizer.getTotalSize();

  return (
    <Paper elevation={1} sx={{ height: '100%', width: '100%' }}>
      <div
        ref={containerRef}
        style={{
          height: 330,
          width: '100%',
          overflow: 'auto',
          position: 'relative',
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'auto',
          contain: 'layout style paint',
        }}
      >
        <Table
          sx={{
            tableLayout: 'fixed',
            width: totalWidth,
            '& .MuiFormControl-root.MuiTextField-root': {
              marginTop: 0,
            },
            '& .MuiInputBase-root.MuiOutlinedInput-root': {
              border: 'none',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            },
          }}
        >
          <TableBody>
            <tr>
              <td
                colSpan={readOnly ? schema.columns : schema.columns + 1}
                style={{
                  padding: 0,

                  height: totalHeight,

                  verticalAlign: 'top',
                }}
              >
                <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableBody>
                    {renderedCells.map((cell) => cell.element)}
                  </TableBody>
                </Table>
              </td>
            </tr>
          </TableBody>
        </Table>
      </div>
    </Paper>
  );
};

export default TimeSlicedVirtualizedTableGrid;
